import { useZero } from '@rocicorp/zero/react';
import { mutators, type CreateTrackArgs } from '@strength/zero-schema';
import type { Schema } from '@strength/zero-schema';
import type {
  ArchiveTrackInput,
  DeleteTrackInput,
  EditTrackInput,
  MoveSubTrackInput,
} from '@strength/domain';
import { trpc } from '../../../../trpc/react';
import { useIsZeroSurfaceActive } from '../../../zero/zero-active';
import {
  TrackMutationError,
  trpcRejectionReason,
  zeroRejectionReason,
} from '../track-mutation-error';

type UseTrackMutationsOptions = {
  /**
   * Runs after a successful create/edit/delete/archive/unarchive/move (both
   * paths).
   */
  onSuccess?: () => void;
};

type UseTrackMutationsResult = {
  createTrack: (args: CreateTrackArgs) => Promise<void>;
  editTrack: (args: EditTrackInput) => Promise<void>;
  /** `teamId` is required so the Zero path can send it on the mutator args (the tRPC path derives it from `teamProcedure` and ignores it). */
  deleteTrack: (args: DeleteTrackInput & { teamId: string }) => Promise<void>;
  archiveTrack: (args: ArchiveTrackInput & { teamId: string }) => Promise<void>;
  unarchiveTrack: (
    args: ArchiveTrackInput & { teamId: string },
  ) => Promise<void>;
  /**
   * Reparent a sub-track onto another parent track on the same team. `teamId`
   * scopes the move on the Zero path (both tracks must belong to it); the tRPC
   * path derives it from `teamProcedure` and ignores it.
   */
  moveSubTrack: (args: MoveSubTrackInput) => Promise<void>;
};

/** Zero mutation envelope: `zero.mutate(...).server` resolves (never rejects) with this. */
type ZeroServerResult =
  | { type: 'success' }
  | { type: 'error'; error: { message: string; details?: unknown } };

/**
 * Dual-path Track mutation hook (mirrors `useTracksQuery`): Zero when active,
 * tRPC otherwise. Hooks run unconditionally (Rules of Hooks); the path is chosen
 * per call. Both paths reject on failure so callers can toast.
 */
export function useTrackMutations(
  options: UseTrackMutationsOptions = {},
): UseTrackMutationsResult {
  const { onSuccess } = options;
  const isZeroActive = useIsZeroSurfaceActive('tracks');
  const zero = useZero<Schema>();
  const trpcCreate = trpc.plan.createTrack.useMutation();
  const trpcEdit = trpc.plan.editTrack.useMutation();
  const trpcDelete = trpc.plan.deleteTrack.useMutation();
  const trpcArchive = trpc.plan.archiveTrack.useMutation();
  const trpcUnarchive = trpc.plan.unarchiveTrack.useMutation();
  const trpcMoveSubTrack = trpc.plan.moveSubTrack.useMutation();

  // Run the active path (Zero thunk invoked only when active, so the optimistic
  // write never runs on the tRPC path), reject on failure, then fire onSuccess.
  const run = async (
    runZero: () => { server: Promise<ZeroServerResult> },
    runTrpc: () => Promise<unknown>,
  ): Promise<void> => {
    if (isZeroActive) {
      const result = await runZero().server;
      if (result.type === 'error') {
        // This path already rethrew a plain Error, so naming the reason costs
        // nothing for the callers that ignore it.
        throw new TrackMutationError(
          result.error.message,
          zeroRejectionReason(result.error),
        );
      }
    } else {
      try {
        await runTrpc();
      } catch (e) {
        const rejectionReason = trpcRejectionReason(e);
        // Anything the server named no reason for keeps its original error:
        // callers and the reporting layer already know how to read a tRPC
        // client error, and re-wrapping one would only change its type.
        if (!rejectionReason) throw e;
        throw new TrackMutationError(
          e instanceof Error ? e.message : '',
          rejectionReason,
        );
      }
    }
    onSuccess?.();
  };

  return {
    createTrack: (args) =>
      run(
        () => zero.mutate(mutators.track.create(args)),
        () => trpcCreate.mutateAsync(args),
      ),
    editTrack: (args) =>
      run(
        () => zero.mutate(mutators.track.edit(args)),
        () => trpcEdit.mutateAsync(args),
      ),
    deleteTrack: (args) =>
      run(
        () => zero.mutate(mutators.track.delete(args)),
        () => trpcDelete.mutateAsync(args),
      ),
    archiveTrack: (args) =>
      run(
        () => zero.mutate(mutators.track.archive(args)),
        () => trpcArchive.mutateAsync(args),
      ),
    unarchiveTrack: (args) =>
      run(
        () => zero.mutate(mutators.track.unarchive(args)),
        () => trpcUnarchive.mutateAsync(args),
      ),
    moveSubTrack: (args) =>
      run(
        () => zero.mutate(mutators.track.move(args)),
        () => trpcMoveSubTrack.mutateAsync(args),
      ),
  };
}
